import apiClient from '@/lib/api/apiClient'
import { showApiError } from '@/lib/utils/notifications'
import type { OrganizationDefaultFormServeType } from '../types'

/**
 * Angular `organization-addedit.downloadFile`: build `url` query value for `serve-file-config`.
 */
export function stripOrganizationLogoPathForServeConfig(filePath: string): string {
  if (!filePath?.trim()) return ''
  try {
    if (filePath.includes('stream-file?path=')) {
      const urlObj = new URL(filePath)
      return decodeURIComponent(urlObj.searchParams.get('path') || '')
    }
    return filePath.replace(/^https?:\/\/[^/]+\/?/, '')
  } catch {
    return ''
  }
}

/**
 * Angular calls `downloadFile(org_id, logoUrl, 'ORGANIZATION_COLLAPSED')` for all three logo slots.
 */
export async function openOrganizationLogoDownload(orgId: number, filePath: string): Promise<void> {
  if (!orgId || !filePath.trim()) return
  const s3Key = stripOrganizationLogoPathForServeConfig(filePath)
  if (!s3Key) return
  const encodedPath = encodeURIComponent(s3Key)
  try {
    const { data } = await apiClient.get<Blob>(
      `/serve-file-config/ORGANIZATION_COLLAPSED/${orgId}/?url=${encodedPath}`,
      { responseType: 'blob' },
    )
    const objectUrl = window.URL.createObjectURL(data)
    window.open(objectUrl, '_blank', 'noopener,noreferrer')
  } catch (error) {
    showApiError(error)
  }
}

export type { OrganizationDefaultFormServeType } from '../types'

/** Angular `organization-addedit.loadOrgDetail` → `storageACHPath` (edit page Default Form tab). */
export const ORGANIZATION_STATIC_ACH_FORM_PATH = '/static/Liberty_ACH_Form.pdf'

/** Angular `organization-addedit.loadOrgDetail` → `storageInstructionPath`. */
export const ORGANIZATION_STATIC_STATEMENT_INSTRUCTION_PATH =
  '/static/LRG_Accessing_Default_Settlement_Statements.pdf'

/** Relative URL for `GET` blob (same pattern as `openOrganizationLogoDownload`). */
export function buildOrganizationDefaultFormServePath(
  orgId: number,
  filePath: string,
  fileType: OrganizationDefaultFormServeType,
): string {
  if (!orgId || !filePath.trim()) return ''
  const s3Key = stripOrganizationLogoPathForServeConfig(filePath)
  if (!s3Key) return ''
  const encodedPath = encodeURIComponent(s3Key)
  return `/serve-file-config/${fileType}/${orgId}/?url=${encodedPath}`
}

/**
 * Angular Default Form: **Download ACH Form** / **Download Statement Instruction File**
 * (`downloadFile(org_id, storagePath, 'ACH_FORM' | 'STATEMENT_INSTRUCTION')`).
 */
export async function openOrganizationDefaultFormDownload(
  orgId: number,
  filePath: string,
  fileType: OrganizationDefaultFormServeType,
): Promise<void> {
  const relativePath = buildOrganizationDefaultFormServePath(orgId, filePath, fileType)
  if (!relativePath) return
  try {
    const { data } = await apiClient.get<Blob>(relativePath, { responseType: 'blob' })
    const objectUrl = window.URL.createObjectURL(data)
    window.open(objectUrl, '_blank', 'noopener,noreferrer')
  } catch (error) {
    showApiError(error)
  }
}
